"use client";

import { type ButtonProps, buttonVariants } from "@/components/ui/button";
import { useSelectItemEvent } from "@/features/analytics/hooks/use-select-item-event";
import { DietCatalog, DietCatalogDetail } from "@/features/diets/types";
import { Link, getLocaleForPathname, getPathname } from "@/i18n/navigation";
import { getConfiguratorParams, objectToQueryString } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import { IconArrowNarrowRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import type { JSX } from "react";

type AddDietToCartProps = {
  diet: DietCatalog | DietCatalogDetail;
  dietSlug: string;
  className?: string;
  button?: ButtonProps & {
    icon?: JSX.Element;
    label?: string;
    variant?: "outline" | "link";
    size?: "sm" | "md" | "lg";
  };
  configuration?: Record<string, string | number | string[]>;
  index?: number;
  kcal?: string;
};

export const AddDietToCart = ({
  diet,
  dietSlug,
  className,
  button,
  configuration = {},
  index,
  kcal,
}: AddDietToCartProps) => {
  const t = useTranslations("diets");
  const searchParams = useSearchParams();
  const configParams = getConfiguratorParams(searchParams);
  const locale = getLocaleForPathname();
  const { sendSelectItemEvent } = useSelectItemEvent();

  return (
    <Link
      data-pw="configure-diet-button-drawer"
      href={
        getPathname({
          href: {
            pathname: "/configure-diet/[slug]",
            params: { slug: dietSlug },
          },
          locale,
        }) + objectToQueryString({ ...configParams, ...configuration })
      }
      className={cn(
        buttonVariants({
          variant: button?.variant || "secondary",
          size: button?.size || "sm",
        }),
        "mt-2 h-10 w-auto rounded-lg px-4 py-2 font-medium md:h-10 xl:h-12",
        className
      )}
      prefetch={true}
      onClick={() => {
        sendSelectItemEvent({
          diet,
          index,
          pagination: false,
          kcal,
        });
      }}
    >
      <span>{t("configure")}</span>
      <IconArrowNarrowRight stroke={1.25} />
    </Link>
  );
};
